import { DependencyList, useEffect, useRef, useState } from 'react'

import { AnimationObject } from 'lottie-react-native'

/**
 * Loads a Lottie animation JSON on demand so the animation data stays out of the
 * importing bundle chunk.
 *
 * Returns null until the data is available.
 * Render a same-sized placeholder to avoid layout shift.
 *
 * The loader must use static `import()` literals so webpack can code-split and Metro can resolve
 * the json file. Metro inlines it into the native bundle and only defers evaluation.
 *
 *   const animationData = useLazyLottieSource(
 *     () => (variant ? import('./a.json') : import('./b.json')),
 *     [variant],
 *   )
 */
export const useLazyLottieSource = (
  load: () => Promise<unknown>,
  deps: DependencyList = [],
): AnimationObject | null => {
  const [animationData, setAnimationData] = useState<AnimationObject | null>(null)
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    let cancelled = false
    setAnimationData(null)
    loadRef
      .current()
      .then((mod) => {
        const data = ((mod as { default?: AnimationObject }).default ?? mod) as AnimationObject
        if (!cancelled) {
          // Clone the data so lottie-web can mutate its own copy without corrupting the original
          setAnimationData(JSON.parse(JSON.stringify(data)))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load Lottie animation', err)
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return animationData
}
